import { useState } from 'react';
import { Calendar, Clock, ArrowRight, RotateCcw, Pencil, Footprints, Eye } from 'lucide-react';
import type { CampaignListItem } from '@/features/campaigns/api/campaignApi';
import {
  formatCampaignDate,
  formatCampaignDuration,
  getCampaignStatusColor,
} from '@/features/campaigns/utils/campaignListUtils';
import { EditCampaignContentModal } from '@/features/campaigns/components/EditCampaignContentModal';
import { getOptimizedImageUrl } from '@/shared/lib/media/optimizeImageUrl';
import { useCan } from '@/shared/hooks/useCan';

type CampaignListCardProps = {
  campaign: CampaignListItem;
  onView?: (campaignId: string) => void;
  onRelaunch?: (campaignId: string) => void;
};

export function CampaignListCard({
  campaign,
  onView,
  onRelaunch,
}: CampaignListCardProps) {
  const canRelaunch = useCan('relaunch-campaign');
  const [editOpen, setEditOpen] = useState(false);
  const id = String(campaign.campaignId || campaign._id || '');
  const title = campaign.campaignName || campaign.name || 'Untitled campaign';
  const status = campaign.status || 'DRAFT';
  const colors = getCampaignStatusColor(status);
  const mediaUrl = getOptimizedImageUrl(campaign?.thumbnail ? campaign.thumbnail : "", { width: 160 });
  const isVideo =
    campaign?.media?.mediaType?.includes('video') ||
    /\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(mediaUrl || '');
  const showRelaunch = canRelaunch && status === 'PUBLISHED' && Boolean(onRelaunch);
  const canEditContent = status === 'PENDING' || status === 'APPROVED';

  return (
    <>
      <article className="group flex flex-col rounded-[24px] bg-white p-5 text-left shadow-[0_10px_30px_rgba(60,40,120,0.05)] transition hover:-translate-y-0.5 hover:shadow-[0_16px_36px_rgba(60,40,120,0.08)]">
        <div className="flex items-start gap-3.5">
          <div className="relative flex h-[60px] w-[60px] shrink-0 items-center justify-center overflow-hidden rounded-[16px] bg-gradient-to-br from-[#2B3E8A] via-[#8156B4] to-[#1E9B6E] text-white">
            {mediaUrl ? (
              isVideo ? (
                <video src={mediaUrl} className="h-full w-full object-cover" />
              ) : (
                <img
                  src={mediaUrl}
                  alt="No media"
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              )
            ) : null}
          </div>

          <div className="min-w-0 flex-1 text-left">
            <div className="mb-1 flex items-start justify-between gap-2">
              <h3 className="m-0 line-clamp-1 text-[16px] font-extrabold leading-snug text-[#3A3660]">
                {title}
              </h3>
              <div className="flex shrink-0 items-center gap-1.5">
                <span
                  className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-extrabold tracking-wide uppercase ${colors.bg} ${colors.text}`}
                >
                  <span className={`h-1.5 w-1.5 rounded-full ${colors.dotBg}`} />
                  {status.replaceAll('_', " ")}
                </span>
                {canEditContent ? (
                  <button
                    type="button"
                    onClick={() => setEditOpen(true)}
                    title="Edit content"
                    aria-label="Edit content"
                    className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-[#EAE8F5] bg-white text-[#6A6688] transition hover:border-[#3B66B8] hover:bg-[#DFE9FA] hover:text-[#3B66B8]"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                ) : null}
              </div>
            </div>
            {campaign.orderId ? (
              <p className="m-0 truncate text-[12px] text-[#6A6688]">
                {campaign.orderId}
              </p>
            ) : null}
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3.5 rounded-[16px] bg-[#F3F2F9] p-3.5 text-left">
          <Meta
            icon={<Calendar className="h-3.5 w-3.5 text-[#8156B4]" />}
            label="Date"
            value={formatCampaignDate(campaign?.startDate)}
          />
          <Meta
            icon={<Clock className="h-3.5 w-3.5 text-[#8156B4]" />}
            label="Duration"
            value={formatCampaignDuration(campaign)}
          />
          <Meta
            icon={<Eye className="h-3.5 w-3.5 text-[#8156B4]" />}
            label="Views"
            value={String(campaign.totalViews ?? '—')}
          />
          <Meta
            icon={<Footprints className="h-3.5 w-3.5" />}
            label="FootFall"
            value={String(campaign.footfallCount ?? '—')}
          />

        </div>

        <div className="mt-auto flex gap-2.5 pt-4">
          <button
            type="button"
            onClick={() => id && onView?.(id)}
            className="dash-btn-gradient inline-flex h-[46px] flex-1 items-center justify-center gap-1.5 rounded-[14px] border-none text-[14px] font-bold text-white transition hover:brightness-105 active:scale-[0.99]"
          >
            View details
            <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
          </button>
          {showRelaunch ? (
            <button
              type="button"
              onClick={() => id && onRelaunch?.(id)}
              className="inline-flex h-[46px] flex-1 items-center justify-center gap-1.5 rounded-[14px] border border-[#D9D3F0] bg-white text-[14px] font-bold text-[#2B3E8A] transition hover:bg-[#F3F2F9] active:scale-[0.99]"
            >
              <RotateCcw className="h-4 w-4" />
              Relaunch
            </button>
          ) : null}
        </div>
      </article>

      <EditCampaignContentModal
        open={editOpen}
        campaign={campaign}
        onClose={() => setEditOpen(false)}
      />
    </>
  );
}

function Meta({
  icon,
  label,
  value,
}: {
  icon?: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div>
      <p className="m-0 flex items-center gap-1 text-[11px] font-semibold tracking-wide text-[#6A6688] uppercase">
        {icon}
        {label}
      </p>
      <p className="m-0 mt-0.5 truncate text-[15px] font-extrabold text-[#3A3660]">
        {value}
      </p>
    </div>
  );
}
